// ═══════════════════════════════════════════════════════════════════════════════
// Helper — settlement history list (by settlement / by money received)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { BudgetListItem, SettlementListItem } from '@fdh/shared';
import { USER_ROLE } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { budgetApi, settlementApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import {
  getFirstDayOfPreviousMonthYmdInTimezone,
  resolveDisplayTimeZone,
} from '../../lib/userTodayYmd';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { StatusFilterChipRow } from '../../components/ui/StatusFilterChipRow';
import { CornerStatusRibbon } from '../../components/ui/CornerStatusRibbon';
import { ListRowChevron } from '../../components/ui/ListRowChevron';
import { LIST_NAV_ROW_CLASS } from '../../components/ui/ListNavRow';
import { HelperEmeraldSegmentTabBar } from '../../components/ui/HelperEmeraldSegmentTabBar';
import { MoneyGivenCombinedCard } from '../../components/settlement/employer/MoneyGivenCombinedCard';
import { settlementListStatusRibbonClass } from '../../utils/listCornerStatusRibbon';
import {
  resolveEmployerMoneyGivenCardView,
  resolveSettlementHistoryBadgeLabel,
} from '../../utils/employerMoneyGivenSettlement';

type HistoryViewTab = 'settlement' | 'money-received';

const EMPLOYER_FINANCE_OPEN_RANGE_END_YMD = '9999-12-31';

const isHistoryViewTab = (value: string | null): value is HistoryViewTab =>
  value === 'settlement' || value === 'money-received';

export const HelperSettlementHistoryPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const timezone = resolveDisplayTimeZone(user?.settings.timezone);

  const [list, setList] = useState<SettlementListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [budgets, setBudgets] = useState<BudgetListItem[]>([]);
  const [settlements, setSettlements] = useState<SettlementListItem[]>([]);
  const [budgetsLoading, setBudgetsLoading] = useState(false);
  const [budgetLoadError, setBudgetLoadError] = useState<string | null>(null);
  const [budgetFilterError, setBudgetFilterError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<SettlementListItem['status'] | 'all'>(
    () =>
      (searchParams.get('status') as SettlementListItem['status'] | 'all' | null) ?? 'all'
  );

  const [startDate, setStartDate] = useState(
    () =>
      searchParams.get('start') ??
      getFirstDayOfPreviousMonthYmdInTimezone(timezone)
  );
  const [endDate, setEndDate] = useState(() => searchParams.get('end') ?? '');

  const activeView: HistoryViewTab = isHistoryViewTab(searchParams.get('view'))
    ? (searchParams.get('view') as HistoryViewTab)
    : 'settlement';

  const isHelper = user?.role === USER_ROLE.HELPER;
  const fromPath = `${location.pathname}${location.search}`;

  const loadSettlementList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await settlementApi.list('all', 1, 50);
      if (res.success && res.data) {
        const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        setList(items);
      } else {
        setListError(res.error ?? t('settlement.error'));
      }
    } catch {
      setListError(t('settlement.error'));
    } finally {
      setListLoading(false);
    }
  }, [t]);

  const loadSettlementsForBudgets = useCallback(async () => {
    try {
      const pageSize = 100;
      const merged: SettlementListItem[] = [];
      for (let page = 1; page <= 20; page += 1) {
        const res = await settlementApi.list('all', page, pageSize);
        if (!res.success || !res.data) {
          break;
        }
        const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        merged.push(...items);
        if (items.length < pageSize) {
          break;
        }
      }
      setSettlements(merged);
    } catch {
      setSettlements([]);
    }
  }, []);

  const loadBudgets = useCallback(async () => {
    setBudgetsLoading(true);
    setBudgetLoadError(null);
    setBudgetFilterError(null);
    if (!startDate) {
      setBudgets([]);
      setBudgetsLoading(false);
      return;
    }
    const effectiveEnd = endDate || EMPLOYER_FINANCE_OPEN_RANGE_END_YMD;
    if (startDate > effectiveEnd) {
      setBudgets([]);
      setBudgetFilterError(t('budget.validation.dates'));
      setBudgetsLoading(false);
      return;
    }
    try {
      const res = await budgetApi.list('all', 1, 100, startDate, effectiveEnd);
      if (res.success && res.data) {
        const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        setBudgets(items);
        return;
      }
      setBudgetLoadError(res.error ?? t('budget.loadError'));
    } catch {
      setBudgetLoadError(t('budget.loadError'));
    } finally {
      setBudgetsLoading(false);
    }
  }, [endDate, startDate, t]);

  useEffect(() => {
    void loadSettlementList();
  }, [loadSettlementList]);

  useEffect(() => {
    if (activeView !== 'money-received') {
      return;
    }
    void loadSettlementsForBudgets();
    void loadBudgets();
  }, [activeView, loadBudgets, loadSettlementsForBudgets]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('start', startDate);
    next.set('end', endDate);
    next.set('status', statusFilter);
    if (activeView !== 'settlement') {
      next.set('view', activeView);
    } else {
      next.delete('view');
    }
    if (next.toString() === searchParams.toString()) {
      return;
    }
    setSearchParams(next, { replace: true });
  }, [activeView, endDate, searchParams, setSearchParams, startDate, statusFilter]);

  const handleViewTabChange = (key: HistoryViewTab) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'settlement') {
      next.delete('view');
    } else {
      next.set('view', key);
    }
    setSearchParams(next, { replace: true });
  };

  const handleNavigateMoneyGivenDetail = (path: string) => {
    navigate(path, { state: { from: fromPath } });
  };

  const formatMoney = (n: number) => (n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`);

  const balanceLabel = (balance: number) => {
    if (balance >= 0) return t('settlement.surplus');
    return t('settlement.overspent');
  };

  const balanceClass = (balance: number) =>
    balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';

  const filteredList = useMemo(() => {
    return list.filter((row) => {
      if (startDate && row.startDate < startDate) {
        return false;
      }
      if (endDate && row.endDate > endDate) {
        return false;
      }
      if (statusFilter !== 'all' && row.status !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [endDate, list, startDate, statusFilter]);

  const sortedBudgets = useMemo(
    () =>
      [...budgets].sort((a, b) => {
        if (a.startDate !== b.startDate) {
          return b.startDate.localeCompare(a.startDate);
        }
        return b.endDate.localeCompare(a.endDate);
      }),
    [budgets]
  );

  const settlementHistoryStatusFilterOptions = useMemo(
    () =>
      (['all', 'submitted', 'reviewed', 'withdrawn', 'rejected', 'unlocked'] as const).map((status) => ({
        value: status,
        label: status === 'all' ? t('receipt.list.filter.all') : t(`settlement.recordStatusFilter.${status}`),
      })),
    [t]
  );

  const historyViewTabs = useMemo(
    () =>
      [
        { key: 'settlement' as const, label: t('settlement.helperHistoryTabBySettlement') },
        { key: 'money-received' as const, label: t('settlement.helperHistoryTabByMoneyReceived') },
      ],
    [t]
  );

  const handleBackClick = () => {
    navigateBack(navigate, '/settlements');
  };

  if (!isHelper) {
    return <Navigate to="/settlements" replace />;
  }

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBackClick}
              aria-label={t('task.action.back')}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4L6 10l6 6" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('settlement.helperHistoryPageTitle')}
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6 pb-24">
        <HelperEmeraldSegmentTabBar
          className="mb-4"
          tabs={historyViewTabs}
          activeKey={activeView}
          onChange={handleViewTabChange}
          ariaLabel={t('settlement.helperHistoryViewTabsAria')}
        />

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <IsoDatePicker
            id="helper-history-start"
            ariaLabel={t('settlement.filterStartDate')}
            value={startDate}
            onChange={setStartDate}
            maxDate={endDate || undefined}
            className="min-w-0 flex-1 basis-[140px]"
            valueFormat="iso"
          />
          <span className="shrink-0 px-0.5 text-sm font-medium text-gray-500 dark:text-gray-400" aria-hidden>
            {t('settlement.helperConfirmDateRangeSeparator')}
          </span>
          <IsoDatePicker
            id="helper-history-end"
            ariaLabel={t('settlement.filterEndDate')}
            value={endDate}
            onChange={setEndDate}
            minDate={startDate || undefined}
            className="min-w-0 flex-1 basis-[140px]"
            valueFormat="iso"
          />
        </div>

        {activeView === 'settlement' ? (
          <>
            <StatusFilterChipRow
              className="mb-4"
              ariaLabel={t('settlement.recordStatusFilterAria')}
              options={settlementHistoryStatusFilterOptions}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            {listLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
              </div>
            ) : listError ? (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {listError}
              </p>
            ) : filteredList.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('settlement.empty')}</p>
            ) : (
              <ul className="space-y-2" role="list">
                {filteredList.map((row) => (
                  <li key={row.id}>
                    <Link
                      to={`/settlements/${row.id}`}
                      state={{ from: fromPath }}
                      className={`${LIST_NAV_ROW_CLASS} p-4 dark:hover:border-primary-600`}
                      tabIndex={0}
                      aria-label={`${t('settlement.period', { start: row.startDate, end: row.endDate })} · ${balanceLabel(row.balance)} ${formatMoney(Math.abs(row.balance))}`}
                    >
                      <CornerStatusRibbon
                        label={resolveSettlementHistoryBadgeLabel(row.status, row.locked, t)}
                        styleClass={settlementListStatusRibbonClass(row.status, row.locked)}
                      />
                      <div className="min-w-0 flex-1 pr-10">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {t('settlement.period', { start: row.startDate, end: row.endDate })}
                        </p>
                        <p className="text-sm">
                          <span className={balanceClass(row.balance)}>
                            {balanceLabel(row.balance)} {formatMoney(Math.abs(row.balance))}
                          </span>
                        </p>
                      </div>
                      <ListRowChevron />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            {budgetFilterError ? (
              <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
                {budgetFilterError}
              </p>
            ) : null}
            {budgetsLoading ? (
              <div className="flex justify-center py-8" role="status" aria-busy="true">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
              </div>
            ) : budgetLoadError ? (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {budgetLoadError}
              </p>
            ) : sortedBudgets.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('budget.empty')}</p>
            ) : (
              <ul className="flex flex-col gap-4" role="list">
                {sortedBudgets.map((row) => {
                  const cardView = resolveEmployerMoneyGivenCardView(row, settlements);
                  return (
                    <li key={row.id}>
                      <MoneyGivenCombinedCard
                        row={row}
                        cardView={cardView}
                        settlements={settlements}
                        fromPath={fromPath}
                        readOnly
                        periodLabel={t('settlement.period', { start: row.startDate, end: row.endDate })}
                        formatCreatedAtLabel={(createdAt) =>
                          t('budget.timeLabel', { value: createdAt ?? '—' })
                        }
                        includesAddOnLabel={t('budget.mergedChildCount', {
                          count: row.mergedChildCount ?? 0,
                        })}
                        moneyGivenAmountLabel={t('settlement.helperStatPeriodBudget')}
                        settlementResultLabel={t('settlement.balance')}
                        mainAmountLabel={t('budget.mainAmountLabel')}
                        subAmountLabel={t('budget.subAmountLabel')}
                        addOnLabel={t('budget.addOn')}
                        editAriaLabel={t('budget.editAria', { start: row.startDate, end: row.endDate })}
                        expandAriaLabel={t('budget.expandChildrenAria', {
                          start: row.startDate,
                          end: row.endDate,
                        })}
                        collapseAriaLabel={t('budget.collapseChildrenAria', {
                          start: row.startDate,
                          end: row.endDate,
                        })}
                        badgeUnsettledLabel={t('settlement.status.unsettled')}
                        badgePendingReviewLabel={t('settlement.status.submitted')}
                        badgeSettledLabel={t('settlement.status.reviewed')}
                        onNavigateDetail={handleNavigateMoneyGivenDetail}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>
    </div>
  );
};
